import { useEffect, useState } from 'react';
import { IHardToMatchDonorReviewResult, ISaveHardToMatchDonorReviewRequest } from '@nkr/models/hard-to-match/hard-to-match-donor-review';
import { IHardToMatchPair } from '@nkr/models/hard-to-match/hard-to-match-pair';
import { IBaseResponse } from '@nkr/models/index';
import { AuthActionCreators } from '@nkr/store/authorization';
import { useAppHookContext } from '@nkr/utils/rest-api';

interface IRetVal {
    list: IHardToMatchPair[],
    loading: boolean,
    error: string,
    refresh: () => Promise<void>,
    saveReview: (request: ISaveHardToMatchDonorReviewRequest) => Promise<string>
}

const useHardToMatchPairs = (): IRetVal => {
    const { dispatch } = useAppHookContext();

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [list, setList] = useState<IHardToMatchPair[]>([]);

    const refresh = async () => {
        setLoading(true);
        setError('');

        let options = {
            url: `${__API__}/admin/hard-to-match/pairs`
        };

        try {
            const response: IBaseResponse = await dispatch(AuthActionCreators.apiGet(options));

            if (response && response.data.success) {
                setList(response.data.results);
            } else {
                setError('Unable to load hard-to-match pairs');
                setList([]);
            }
        } catch (err) {
            setList([]);
            setError(err?.response?.data || err?.message || 'Unable to load hard-to-match pairs');
        } finally {
            setLoading(false);
        }
    };

    const saveReview = async (request: ISaveHardToMatchDonorReviewRequest): Promise<string> => {
        let options = {
            url: `${__API__}/admin/hard-to-match/review`,
            data: request
        };

        try {
            const response: IBaseResponse = await dispatch(AuthActionCreators.apiPost(options));

            if (!response?.data.success) {
                return response?.data.message || 'Unable to save review';
            }

            const review: IHardToMatchDonorReviewResult = response.data.results;

            setList(current => current.map(x =>
                x.recipientId == request.recipientId && x.donorId == request.donorId ? { ...x, ...review } : x
            ));

            return '';
        } catch (err) {
            return err?.response?.data || err?.message || 'Unable to save review';
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    return { list, loading, error, refresh, saveReview }
}

export default useHardToMatchPairs;
